import type { RouteContext } from 'universal-router';

import { GENERAL_SETTINGS_PAGE } from 'src/constants';
import {
  findSettingsPageByLegacyPath,
  findSettingsPageByPath,
} from 'src/features/settings/registry/settingsRegistry';
import { isRouteActionContext, RouteAction } from 'src/routes/types';
import { getClientSettingsTerm } from 'src/utils/clientSettingsPaths';

const isNewSettingsEnabled = (flags: unknown): boolean =>
  typeof flags === 'object' &&
  flags !== null &&
  'EnableNewSettings' in flags &&
  flags.EnableNewSettings === true;

/**
 * A route for a page that only exists under `new-settings`, such as one split
 * out of an old page. With the flag off it matches nothing, so the path falls
 * through to not found as it did before the page existed.
 */
export const newSettingsOnlyRoute = (
  path: string,
  load: () => Promise<{ default: RouteAction }>,
) => ({
  path,
  async action(context: RouteContext) {
    if (
      !isNewSettingsEnabled(context.flags) ||
      !isRouteActionContext(context)
    ) {
      return undefined;
    }
    const { default: action } = await load();
    return action(context);
  },
});

const isFlagOn = (flags: unknown, flag: string): boolean =>
  typeof flags === 'object' &&
  flags !== null &&
  Object.entries(flags).some(([key, value]) => key === flag && value === true);

/**
 * Like `newSettingsOnlyRoute`, but a page behind a second flag. With either
 * flag off the path goes to General instead of not found, since the page has
 * a sidebar entry people may have bookmarked.
 */
export const newSettingsFlaggedRoute = (
  path: string,
  flag: string,
  load: () => Promise<{ default: RouteAction }>,
) => ({
  path,
  async action(context: RouteContext) {
    if (!isRouteActionContext(context)) return undefined;
    if (
      !isNewSettingsEnabled(context.flags) ||
      !isFlagOn(context.flags, flag)
    ) {
      return { redirect: GENERAL_SETTINGS_PAGE.path };
    }
    const { default: action } = await load();
    return action(context);
  },
});

/**
 * Sends old settings URLs to the page that replaced them, straight off each
 * registry entry's `legacyPaths`. It must stay the first child of /settings so
 * it runs before the old page's own route; with `new-settings` off the old
 * pages are still real, so it matches nothing.
 */
export const settingsLegacyPathRedirectRoute = {
  path: '/(.*)',
  action(context: RouteContext) {
    if (
      !isNewSettingsEnabled(context.flags) ||
      !isRouteActionContext(context)
    ) {
      return undefined;
    }

    const page = findSettingsPageByLegacyPath(
      context.pathname,
      getClientSettingsTerm(context.portalConfig.features.customLabels),
    );
    if (!page) return undefined;

    return {
      redirect: `${page.path}${context.search ?? ''}${context.hash ?? ''}`,
    };
  },
};

/**
 * Serves registry pages that no hand-written route claims, so one registry
 * entry is enough to make a page routable. It must stay the last child of
 * /settings; with `new-settings` off it matches nothing and the request falls
 * through to the not-found page as before.
 */
export const settingsRegistryRoute = {
  path: '/(.*)',
  async action(context: RouteContext) {
    if (
      !isNewSettingsEnabled(context.flags) ||
      !isRouteActionContext(context)
    ) {
      return undefined;
    }

    const clientTerm = getClientSettingsTerm(
      context.portalConfig.features.customLabels,
    );
    const page = findSettingsPageByPath(context.pathname, clientTerm);
    if (!page) return undefined;

    const { getRegistrySettingsPageResult } = await import(
      /* webpackChunkName: 'settings-registry-page' */ './registry-page'
    );
    return getRegistrySettingsPageResult(page);
  },
};
